import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { AuthPageLayoutComponent } from '../../../shared/layout/auth-page-layout/auth-page-layout.component';
import { LabelComponent } from '../../../shared/components/form/label/label.component';
import { InputFieldComponent } from '../../../shared/components/form/input/input-field.component';
import { InvitationApiService } from '../services/invitation-api.service';
import { InvitationResponse } from '../models/invitation.model';

/** Shape del error del backend para invitaciones (400/404 con `details` opcional). */
interface InvitationErrorLike {
  status?: number;
  error?: {
    message?: string;
    details?: {
      /** REVOKED | EXPIRED | ACCEPTED */
      invitationStatus?: string;
      /** DELETED | INACTIVE (inc16) */
      businessState?: string;
    };
  };
}

@Component({
  selector: 'app-accept-invite',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    AuthPageLayoutComponent,
    LabelComponent,
    InputFieldComponent,
  ],
  templateUrl: './accept-invite.component.html',
})
export class AcceptInviteComponent implements OnInit {
  token = '';
  invitation: InvitationResponse | null = null;
  fullname = '';
  lastname = '';
  password = '';
  showPassword = false;
  loading = false;
  submitting = false;
  error = '';
  errorTitle = 'Invitación no válida';
  /**
   * Acción sugerida bajo el mensaje de error.
   * - `REQUEST_INVITE`: la invitación dejó de servir, pero la empresa sigue operativa.
   * - `CONTACT_ADMIN`: la empresa fue eliminada o está inactiva (inc16) — pedir otra
   *   invitación no soluciona nada, hay que hablar con un administrador.
   */
  errorAction: 'REQUEST_INVITE' | 'CONTACT_ADMIN' = 'REQUEST_INVITE';
  success = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private invitationApiService: InvitationApiService,
  ) {}

  ngOnInit(): void {
    const token = this.route.snapshot.paramMap.get('token');
    if (!token) {
      this.error = 'Token de invitación no válido.';
      return;
    }
    this.token = token;
    this.loading = true;
    this.invitationApiService.getByToken(token).subscribe({
      next: (invitation) => {
        this.invitation = invitation;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.applyInvitationError(err, 'No se pudo validar la invitación.');
      },
    });
  }

  /**
   * Traduce el error del backend a un estado claro para el usuario.
   * El backend devuelve `message` en español y opcionalmente:
   * - `details.invitationStatus` (REVOKED | EXPIRED | ACCEPTED) — tiene prioridad.
   * - `details.businessState` (DELETED | INACTIVE) — inc16: la empresa destino ya no
   *   admite altas, así que el CTA es contactar a un administrador.
   */
  private applyInvitationError(
    err: InvitationErrorLike,
    fallback: string,
  ): void {
    const invitationStatus = err?.error?.details?.invitationStatus;
    const businessState = err?.error?.details?.businessState;

    if (invitationStatus) {
      this.errorTitle = this.titleForStatus(invitationStatus);
      this.errorAction = 'REQUEST_INVITE';
    } else if (businessState) {
      this.errorTitle = this.titleForBusinessState(businessState);
      this.errorAction = 'CONTACT_ADMIN';
    } else {
      this.errorTitle = 'Invitación no válida';
      this.errorAction = 'REQUEST_INVITE';
    }

    this.error = err?.error?.message?.trim() || fallback;
  }

  /** La invitación ya no es utilizable: hay que salir del formulario y mostrar el error. */
  private isInvitationBlocked(err: InvitationErrorLike): boolean {
    return (
      err?.status === 404 ||
      !!err?.error?.details?.invitationStatus ||
      !!err?.error?.details?.businessState
    );
  }

  private titleForStatus(status?: string): string {
    switch (status) {
      case 'REVOKED':
        return 'Invitación revocada';
      case 'EXPIRED':
        return 'Invitación expirada';
      case 'ACCEPTED':
        return 'Invitación ya utilizada';
      default:
        return 'Invitación no válida';
    }
  }

  private titleForBusinessState(state?: string): string {
    switch (state) {
      case 'DELETED':
        return 'Empresa eliminada';
      case 'INACTIVE':
        return 'Empresa inactiva';
      default:
        return 'Invitación no válida';
    }
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  onSubmit(): void {
    if (
      !this.invitation ||
      !this.fullname ||
      !this.lastname ||
      !this.password
    ) {
      return;
    }
    if (this.password.length < 8) {
      this.error = 'La contraseña debe tener al menos 8 caracteres.';
      return;
    }
    this.submitting = true;
    this.error = '';
    const request = {
      invitationToken: this.token,
      fullname: this.fullname,
      lastname: this.lastname,
      password: this.password,
    };
    this.invitationApiService.registerWithInvitation(request).subscribe({
      next: () => {
        this.success = true;
        this.submitting = false;
        setTimeout(() => {
          this.router.navigate(['/signin']);
        }, 2000);
      },
      error: (err) => {
        this.submitting = false;
        const backendMsg = err?.error?.message?.trim();

        // Si la invitación dejó de ser válida (revocada/expirada/aceptada), no existe,
        // o la empresa fue eliminada/desactivada (inc16), ya no tiene sentido mostrar
        // el formulario: cambiamos al estado de error.
        if (this.isInvitationBlocked(err)) {
          this.invitation = null;
          this.applyInvitationError(
            err,
            'El enlace de invitación no es válido.',
          );
          return;
        }

        // 409 (correo ya registrado) u otros: mostramos el mensaje del backend en línea.
        this.error = backendMsg || 'No se pudo completar el registro.';
      },
    });
  }

  // mapea los estados de invitación a mensajes de error específicos

  private readonly INVITATION_STATUS_MESSAGES: Record<
    string,
    { title: string; message: string }
  > = {
    REVOKED: {
      title: 'Invitación revocada',
      message:
        'Su invitación ha sido revocada y ya no tiene acceso al sistema. Comuníquese con un administrador para solicitar una nueva invitación.',
    },
    EXPIRED: {
      title: 'Invitación expirada',
      message:
        'Esta invitación ha expirado. Solicite una nueva invitación a un administrador.',
    },
    USED: {
      title: 'Invitación ya utilizada',
      message:
        'Esta invitación ya fue utilizada. Si ya tienes una cuenta, inicia sesión normalmente.',
    },
    ACCEPTED: {
      title: 'Invitación ya utilizada',
      message:
        'Esta invitación ya fue utilizada. Si ya tienes una cuenta, inicia sesión normalmente.',
    },
  };

  private parseInvitationStatus(
    backendMessage: string | undefined,
  ): string | null {
    if (!backendMessage) return null;
    const match = backendMessage.match(/current status:\s*([A-Z_]+)/i);
    return match ? match[1].toUpperCase() : null;
  }
}
